import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl, wfsBaseUrl } from '../../network/urls';
import { resolveApiOfficeCode } from '../../utils/adminCompany';

const CACHE_EXPIRY_HOURS = 24;
const geoJsonMemoryCache = new Map();

const getCachedGeoJson = (officeCode) => {
    const entry = geoJsonMemoryCache.get(String(officeCode));
    if (!entry) return null;
    if (Date.now() - entry.timestamp >= CACHE_EXPIRY_HOURS * 60 * 60 * 1000) {
        geoJsonMemoryCache.delete(String(officeCode));
        return null;
    }
    return entry.data;
};

const setCachedGeoJson = (officeCode, data) => {
    geoJsonMemoryCache.set(String(officeCode), {
        data,
        timestamp: Date.now(),
    });
};

const initialState = {
    data: null,
    isLoading: false,
    error: null,
    officeCode: '',
};

export const fetchGeoJson = createAsyncThunk(
    'complaints/fetchGeoJsonArray',
    async ({ subDivisionCode, roleTypeId }, { getState }) => {

        const { auth } = getState();
        const { wmsPrivateToken, code, hierarchy, token, roleId, selectedAdminCompanyCode } = auth;

        let codeToApi = subDivisionCode
        const codeStr = codeToApi.toString();
        const codeLength = codeStr.length;

        // Only slice to company code (2 digits) when:
        // - roleTypeId is 1 (Technical user)
        // - AND code length is <= 3 (company or circle level, not division or subdivision)
        // For subdivision codes (7 digits), we need the full code to get feeder lines
        if (roleTypeId == 1 && codeLength <= 3) {
            codeToApi = codeStr.slice(0, 2)
        }

        const finalCode = resolveApiOfficeCode(
            code,
            roleId,
            codeToApi,
            selectedAdminCompanyCode,
        );

        // Check cache first
        const cachedData = getCachedGeoJson(finalCode);
        if (cachedData) {
            return { data: cachedData, fromCache: true, officeCode: String(finalCode) };
        }


        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "officeCode": finalCode
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch(cfpBaseUrl + "lineComplaints/getGISMapping", requestOptions);
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Geo Json API issue!');
        }

        const result = await response.json();

        if (result.Message === "Data Not Found.") {
            throw new Error(result.Message, 'printing error message' || 'Unable to fetch stats!');
        }

        // Cache the result
        if (result.data) {
            setCachedGeoJson(finalCode, result.data);
        }

        return { ...result, officeCode: String(finalCode) };
    }
);

const geoJsonSlice = createSlice({
    name: 'geoJsonArray',
    initialState: initialState,
    reducers: {
        resetGeoJsonState(state) {
            state.data = null;
            state.isLoading = false;
            state.error = null;
            state.officeCode = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGeoJson.pending, (state, action) => {
                state.isLoading = true;
                const nextCode = action.meta?.arg?.subDivisionCode?.toString() || '';
                if (nextCode && state.officeCode && nextCode.slice(0, 2) !== String(state.officeCode).slice(0, 2)) {
                    state.data = null;
                }
                if (nextCode) {
                    state.officeCode = nextCode;
                }
            })
            .addCase(fetchGeoJson.fulfilled, (state, action) => {
                const responseCode = action.payload?.officeCode?.toString()
                    || action.meta?.arg?.subDivisionCode?.toString()
                    || '';
                // Ignore late response from a previous DISCO
                if (
                    state.officeCode &&
                    responseCode &&
                    String(state.officeCode).slice(0, 2) !== String(responseCode).slice(0, 2)
                ) {
                    return;
                }
                state.isLoading = false;
                if (action.payload) {
                    state.data = action.payload.fromCache ? action.payload.data : action.payload.data;
                } else {
                    state.data = null;
                }
                state.officeCode = responseCode || state.officeCode;
                state.error = null;
            })
            .addCase(fetchGeoJson.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetGeoJsonState } = geoJsonSlice.actions;

export default geoJsonSlice.reducer;
